import React, { useMemo } from 'react';
import type { AvatarGroupProps, AvatarGroupOverrides, AvatarGroupContextValue } from './Avatar.types';
import { Box } from '../Box';
import { styled } from '@withjoy/joykit/theme';
import { resolveGroupSpacing } from './Avatar.styles';
import { useOverrides } from '@shared/utils/overrides';
import { forwardRef } from '@shared/utils/forwardRef';
import { Avatar, AvatarFallback } from './Avatar';
import { AvatarGroupContext } from './AvatarGroupContext';

const StyledGroupRoot = styled(Box)`
  display: inline-flex;
  align-items: center;

  [data-slot='avatar'] {
    box-shadow: var(--avatar-group-ring);
  }

  [data-slot='avatar'] + [data-slot='avatar'] {
    margin-inline-start: var(--avatar-group-gap);
  }
`;

const EMPTY_OVERRIDES: AvatarGroupOverrides = {};
const ROOT_DEFAULTS = { Root: StyledGroupRoot };

const AvatarGroup = forwardRef<'div', AvatarGroupProps>((props, ref) => {
  const { max, size = 'md', spacing: spacingProp, ringColor = 'white', overrides = EMPTY_OVERRIDES, children, ...restProps } = props;

  const {
    Root: [Root, rootProps],
  } = useOverrides(ROOT_DEFAULTS, { Root: overrides.Root });

  // toArray re-keys children with a `.$` prefix, but keys remain stable across
  // renders so this won't cause spurious unmounts.
  const validChildren = React.Children.toArray(children).filter(React.isValidElement);
  const totalCount = validChildren.length;
  const clampedMax = max != null ? Math.max(0, max) : totalCount;
  const visibleChildren = validChildren.slice(0, clampedMax);
  const excessCount = totalCount - clampedMax;

  const gap = spacingProp ?? resolveGroupSpacing(size);

  const groupCtx = useMemo<AvatarGroupContextValue>(() => ({ size }), [size]);

  return (
    <AvatarGroupContext.Provider value={groupCtx}>
      <Root
        ref={ref}
        data-slot="avatar-group"
        role="group"
        style={
          {
            '--avatar-group-ring': `0 0 0 2px ${ringColor}`,
            '--avatar-group-gap': `${gap}px`,
          } as React.CSSProperties
        }
        {...rootProps}
        {...restProps}
      >
        {visibleChildren}
        {excessCount > 0 && (
          <Avatar size={size}>
            <AvatarFallback>
              <span style={{ marginInlineStart: '-0.3em' }}>+{excessCount}</span>
            </AvatarFallback>
          </Avatar>
        )}
      </Root>
    </AvatarGroupContext.Provider>
  );
});

AvatarGroup.displayName = 'AvatarGroup';

export { AvatarGroup };
